import {Model} from "mobx-rest/lib";
import {NestedModel} from "../../nestedmodel";
import has from "lodash/has";
import differenceWith from "lodash/differenceWith";
import includes from "lodash/includes";
import isEqual from "lodash/isEqual";
import isEmpty from "lodash/isEmpty";
import set from "lodash/set";
import {debug_log, debug_error} from '../../../logger';
import {objDiff} from "utils/dist/objDiff";
import { TsmApi } from "../../../index";

// TODO: USC-690: The body of the POST /deviceFeatureSets/deviceOp/{cpeId}
//       endpoint is sent to the POST /devices/{cpeID}/tasks/executeGroovyScript
//       endpoint as the 'featureSetActionsMap' script parameter.
// TODO: There's a success modal on "CPE offline" error that needs to be fixed.

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const SAVE_TIMEOUT = 2 * MINUTE;

const getApiClient = (model) => {
    const url = new URL(model.get("request_url"));
    const apiPath = url.pathname.split(model.url())[0];
    const api = new TsmApi(apiPath);
    return api;
};

const getUxConfig = async (model) => {
    if (model.uxConfig) {
        return model.uxConfig;
    }
    const api = getApiClient(model);
    const uxConfig = await api.deviceFeatureSets.uxConfigAsMap
        .cpeId(cpeId)
        .fetch();
    return uxConfig;
};


class _CpeId extends Model {
    constructor(props, attributes) {
        super(props, Object.assign({}, attributes));
        this.value = props.value;
    }

    urlRoot() {
        return '/deviceFeatureSets/deviceOp';
    }

    url() {
        return [this.urlRoot(), this.value].join('/');
    }
}

export class CpeId extends NestedModel {
    constructor(props, attributes) {
        super(props, Object.assign({}, attributes));
        this._originalObject = null;
        this._deviceFeatureSets = {};
        this.uxConfig = null;
    }

    async fetch(data) {
        const self = this;
        // return super.fetch().then( val => {self._originalObject = val } )
        return super.fetch(data).then((data) => {
            // self._originalObject = self.toJS();
            self._originalObject = JSON.parse(
                JSON.stringify(self.committedAttributes)
            );
            return data;
        });
    }

    updateFSDataWithResult = (result) => {
        const featureSets = this.get("data").featureSets;
        for (const fsName in result.data) {
            for (const propName in result.data[fsName].uxData) {
                featureSets[fsName].uxData[propName] =
                    result.data[fsName].uxData[propName];
            }
        }
    };

    updateFSDataWithTaskResult = (result) => {
        const featureSets = this.get("data").featureSets;
        for (const fsName in result.responseObject.data) {
            for (const propName in result.responseObject.data[fsName].uxData) {
                featureSets[fsName].uxData[propName] =
                    result.responseObject.data[fsName].uxData[propName];
            }
        }
    };

    async poll(data) {
        const api = getApiClient(this);
        if (!has(data, "taskResultInfo")) {
            return data;
        }
        this.taskId = data.taskResultInfo.taskId;
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                api.tasks
                    .taskId(data.taskResultInfo.taskId)
                    .executeGroovyScript.fetch()
                    .then(this.poll.bind(this))
                    .then(resolve)
                    .catch(reject);
            }, data.taskResultInfo.pollIntervalMs);
        });
    }

    async saveAsTask(data) {
        const api = getApiClient(this);
        const cpeId = this.value;
        return api.devices
            .cpeId(cpeId)
            .tasks.executeGroovyScript.save({
                scriptName: "DeviceFeatureSetMapperRestApiMultiAction",
                parameterList: [
                    {
                        name: "featureSetActionsMap",
                        value: JSON.stringify(data || {}),
                    },
                ],
                taskResultTimeoutSecs: 0,
            })
            .then(this.poll.bind(this));
    }

    async save(attributes, options) {
        // const actions = await this.getSaveActions();
        if (!this.uxConfig) {
            this.uxConfig = await getUxConfig(this);
        }
        const changedFeatureSets = this._changedFeatureSets();
        const actions = this._buildPostActions(changedFeatureSets);

        debug_log("Changed:", changedFeatureSets);
        debug_log("POST actions:", actions);

        const handleOffline = (result) => {
            if (
                !window.navigator.onLine &&
                result?.error === null &&
                result?.requestResponse === null
            ) {
                return new Promise((resolve, reject) => {
                    const timeout = setTimeout(() => {
                        window.removeEventListener("online", handleOnline);
                        reject(new Error("Timeout waiting for reconnect"));
                    }, SAVE_TIMEOUT);
                    const handleOnline = () => {
                        clearTimeout(timeout);
                        window.removeEventListener("online", handleOnline);
                        if (this.taskId) {
                            return resolve(this.poll({ taskResultInfo: { taskId: this.taskId } }));
                        }
                        resolve(this.save(attributes, options));
                    };
                    window.addEventListener("online", handleOnline);
                });
            }
            if (!result.responseObject) {
                return Promise.reject(result);
            }
            return result;
        };

        const handleSuccess = (result) => {
            debug_log("Result", result);
            this.updateFSDataWithTaskResult(result);
            debug_log("Model changes", this.changes);
            this.commitChanges();
            // this._originalObject = this.toJS();
            this._originalObject = JSON.parse(
                JSON.stringify(this.committedAttributes)
            );
            return result;
        };

        const handleError = (result) => {
            debug_error("Error", result);
            throw result;
        };

        return this.saveAsTask(actions)
            .catch(handleOffline)
            .then(handleSuccess)
            .catch(handleError)
            .finally(() => {this.taskId = null});
    }

    // Legacy save
    // async save(attributes, options) {
    async saveSingleRequest(attributes, options) {
        // const actions = await this.getSaveActions();
        if (!this.uxConfig) {
            this.uxConfig = await getUxConfig(this);
        }
        const changedFeatureSets = this._changedFeatureSets();
        const actions = this._buildPostActions(changedFeatureSets);

        const handleOffline = (result) => {
            if (
                !window.navigator.onLine &&
                result?.error === null &&
                result?.requestResponse === null
            ) {
                return new Promise((resolve, reject) => {
                    const timeout = setTimeout(() => {
                        window.removeEventListener("online", handleOnline);
                        reject(new Error("Timeout waiting for reconnect"));
                    }, SAVE_TIMEOUT);
                    const handleOnline = () => {
                        clearTimeout(timeout);
                        window.removeEventListener("online", handleOnline);
                        resolve(this.save(attributes, options));
                    };
                    window.addEventListener("online", handleOnline);
                });
            }
            if (!result.responseObject) {
                return Promise.reject(result);
            }
            return result;
        };

        const handleSuccess = (result) => {
            debug_log("Result", result);
            this.updateFSDataWithResult(result);
            debug_log("Model changes", this.changes);
            this.commitChanges();
            // this._originalObject = this.toJS();
            this._originalObject = JSON.parse(
                JSON.stringify(this.committedAttributes)
            );
            return result;
        };

        debug_log("Changed:", changedFeatureSets);
        debug_log("POST actions:", actions);
        const _cpeId = new _CpeId({ value: this.value });
        return _cpeId
            .rpc("", actions)
            .then((result) => result)
            .catch(handleOffline)
            .then((result) => {
                return handleSuccess(result);
            })
            .catch((error) => {
                debug_error(error);
                throw error;
            });
    }

    featureSetId(value) {
        // TODO: Implement PUT and POST for featureSetId model
        if (!this._deviceFeatureSets[value]) {
            this._deviceFeatureSets[value] = new NestedModel({
                value: value,
                parent: this,
            });
        }
        return this._deviceFeatureSets[value];
    }

    _changedFeatureSets() {
        if (!has(this.changes, "data.featureSets")) {
            debug_log("No featureSets to post");
            return;
        }
        const changedFeatureSets = {};
        const model = this.toJS();
        debug_log(
            "Original model",
            JSON.parse(JSON.stringify(this._originalObject))
        );
        debug_log(
            "Current model",
            JSON.parse(JSON.stringify(this.committedAttributes))
        );
        debug_log("Model.changes", JSON.parse(JSON.stringify(this.changes)));
        for (const key of Object.keys(this.changes.data.featureSets)) {
            debug_log("Processing feature set:", key);
            switch (key) {
                case "reservedIps":
                case "bedtimePC":
                case "portMapping":
                case "pppAccounts":
                    // const alarmIdPropName = ["reservedIps", "bedtimePC"].includes(key)
                    const alarmIdPropName = ["reservedIps"].includes(key)
                        ? "instanceId"
                        : this.uxConfig.data.featureSets[key].alarmIdPropName;
                    // console.log("alarmIdPropName", alarmIdPropName);
                    changedFeatureSets[key] = {};
                    // Filter out all unchanged entries
                    // Deal with changed, added, deleted entries
                    let changed =
                        this.changes.data.featureSets[key].uxData;
                    let original =
                        this._originalObject.data.featureSets[key].uxData;

                    // console.log("changed", changed);
                    // console.log("original", original);

                    const changedAndAdded = differenceWith(
                        changed,
                        original,
                        isEqual
                    );
                    // console.log("changedAndAdded", changedAndAdded);
                    const removedAndChanged = differenceWith(
                        original,
                        changed,
                        isEqual
                    );
                    // console.log("removedAndChanged", removedAndChanged);
                    const added = changed.filter(
                        (obj) => !has(obj, alarmIdPropName)
                    );
                    // console.log("added", added);
                    const changedObjects = differenceWith(
                        changedAndAdded,
                        added
                    );
                    // console.log("changedObjects", changedObjects);
                    const missing = differenceWith(
                        removedAndChanged,
                        changedAndAdded,
                        (a, b) => a[alarmIdPropName] == b[alarmIdPropName]
                    );
                    // console.log("missing", missing);

                    changedFeatureSets[key].added = added;
                    changedFeatureSets[key].changed = changedObjects;
                    changedFeatureSets[key].missing = missing;
                    break;

                case "wifi_24G":
                case "wifi_50G":
                case "wifi_60G":
                case "wifi_24G_guest":
                case "wifi_24G_guest_1":
                case "wifi_24G_guest_2":
                case "wifi_24G_guest_3":
                case "wifi_24G_guest_4":
                case "wifi_24G_guest_5":
                case "wifi_24G_guest_6":
                case "wifi_24G_guest_7":
                case "wifi_24G_guest_8":
                case "wifi_24G_guest_9":
                case "wifi_24G_guest_10":
                case "wifi_50G_guest":
                case "wifi_50G_guest_1":
                case "wifi_50G_guest_2":
                case "wifi_50G_guest_3":
                case "wifi_50G_guest_4":
                case "wifi_50G_guest_5":
                case "wifi_50G_guest_6":
                case "wifi_50G_guest_7":
                case "wifi_50G_guest_8":
                case "wifi_50G_guest_9":
                case "wifi_50G_guest_10":
                case "wifi_60G_guest":
                case "wifi_60G_guest_1":
                case "wifi_60G_guest_2":
                case "wifi_60G_guest_3":
                case "wifi_60G_guest_4":
                case "wifi_60G_guest_5":
                case "wifi_60G_guest_6":
                case "wifi_60G_guest_7":
                case "wifi_60G_guest_8":
                case "wifi_60G_guest_9":
                case "wifi_60G_guest_10":
                case "wifi_24G_backhaul":
                case "wifi_50G_backhaul":
                case "wifi_60G_backhaul":
                    const wifiOriginal =
                        this._originalObject.data.featureSets[key].uxData;
                    const wifiUpdated = model.data.featureSets[key].uxData;
                    const wifiChanges =
                        this.changes.data.featureSets[key].uxData;
                    changedFeatureSets[key] = objDiff(
                        wifiOriginal,
                        wifiUpdated
                    );
                    if (
                        has(wifiChanges, "passphrase") &&
                        !has(wifiChanges, "securityType")
                    ) {
                        changedFeatureSets[key].changed.securityType =
                            wifiOriginal.securityType;
                    }
                    if (
                        has(wifiChanges, "securityType") &&
                        !has(wifiChanges, "passphrase") &&
                        includes(wifiChanges.securityType, "WPA3")
                    ) {
                        changedFeatureSets[key].changed.passphrase =
                            wifiOriginal.passphrase;
                    }
                    if (wifiChanges.autoChannel) {
                        changedFeatureSets[key].changed.channel = 0;
                    }
                    if (
                        has(wifiChanges, "autoChannel") &&
                        !wifiChanges.autoChannel
                    ) {
                        changedFeatureSets[key].changed.channel =
                            wifiUpdated.channel;
                    }
                    if (
                        (key.includes("wifi_50G") || key.includes("wifi_60G")) &&
                        has(
                            model,
                            "data.featureSets.mesh_wifi.uxData.enabled"
                        ) &&
                        model.data.featureSets.mesh_wifi.uxData.enabled
                    ) {
                        delete changedFeatureSets[key].changed.ssid;
                        delete changedFeatureSets[key].changed.securityType;
                        delete changedFeatureSets[key].changed.passphrase;
                    }
                    break;

                case "wan":
                case "ipv6":
                    // We want to send all DNS servers even if only one got
                    // updated. Note that this is different than respecting the
                    // "required" flag as we don't want to send dns1-3 values if
                    // none of these got updated.
                    changedFeatureSets[key] = objDiff(
                        this._originalObject.data.featureSets[key].uxData,
                        model.data.featureSets[key].uxData
                    );
                    const dnsList = ["dns1", "dns2", "dns3"];
                    const isDnsChanged =
                        dnsList.find((dnsKey) => {
                            return has(changedFeatureSets[key].changed, dnsKey);
                        }) !== undefined;

                    if (isDnsChanged) {
                        dnsList.forEach((dnsKey) => {
                            if (!has(changedFeatureSets[key].changed, dnsKey)) {
                                changedFeatureSets[key].changed[dnsKey] =
                                    this._originalObject.data.featureSets[
                                        key
                                    ].uxData[dnsKey];
                            }
                        });
                    }
                    break;

                default:
                    changedFeatureSets[key] = objDiff(
                        this._originalObject.data.featureSets[key].uxData,
                        model.data.featureSets[key].uxData
                    );
                    break;
            }

            // Add all attributes marked as 'required' in the uxConfig to the changed object.
            const uxConfig = this.uxConfig.data;
            Object.values(uxConfig.featureSets[key].propertyMapping)
                .filter((prop) => prop.required)
                .forEach((prop) => {
                    if (!has(changedFeatureSets[key].changed, prop.propName)) {
                        changedFeatureSets[key].changed[prop.propName] =
                            this._originalObject.data.featureSets[key].uxData[
                                prop.propName
                            ];
                    }
                });
        }
        return changedFeatureSets;
    }

    _buildPostActions(changedFeatureSets) {
        const actions = {};
        for (const featureSet in changedFeatureSets) {
            const added = changedFeatureSets[featureSet].added;
            if (!isEmpty(added)) {
                set(actions, "addActions." + featureSet + ".uxData", added);
            }
            const changed = changedFeatureSets[featureSet].changed;
            if (!isEmpty(changed)) {
                set(actions, "setActions." + featureSet + ".uxData", changed);
            }
            const missing = changedFeatureSets[featureSet].missing;
            if (!isEmpty(missing)) {
                set(actions, "delActions." + featureSet + ".uxData", missing);
            }
        }
        return actions;
    }
}

